<template>
  <div class="login-form">
    <div class="background-form">
      <div class="form">
        <div class="container-form">
          <img class="logo-print" src="../assets/logo printstat cloud.png" alt="printstat cloud">
          <div class="login-header">{{ isAuthentication ? $t('authentication.loginFormHeader') : $t('authentication.forgotPassFormHeader') }}</div>

          <div
            v-if="isAuthentication"
            class="authentication-form"
          >
            <dx-form
              :form-data="userCredentials"
              :read-only="loading"
              labelMode="floating"
              @initialized="saveFormInstance"
            >
              <dx-simple-item
                data-field="login"
                :editor-options="editorsOptions.login"
              >
                <dx-label :text="$t('authentication.login')"/>
                <dx-required-rule :message="$t('general.mandatory')"/>
              </dx-simple-item>

              <dx-simple-item
                :editor-options="editorsOptions.password"
                data-field="password"
              >
                <dx-label :text="$t('authentication.password')"/>
                <dx-required-rule :message="$t('general.mandatory')"/>
                <dx-validation-rule
                  type="pattern"
                  :pattern="validationPatterns.password"
                  :message="$t('general.badPassword')"
                />
              </dx-simple-item>

              <dx-group-item :colCount="2">
                <dx-simple-item>
                  <template #default>
                    <div class="dx-field">
                      <dx-check-box :value.sync="userCredentials.rememberUser" :text="$t('authentication.remember')" />
                    </div>
                  </template>
                </dx-simple-item>

                <dx-simple-item>
                  <template #default>
                    <span class="link" @click="forgotPassClick()">{{ $t('authentication.forgotPasswordText') }}</span>
                  </template>
                </dx-simple-item>
              </dx-group-item>
            </dx-form>

            <div class="dx-field">
              <dx-button
                id="submit"
                type="default"
                width="100%"
                @click="onLoginClick"
                :disabled="loading"
              >
                <template>
                  <div class="button-container">
                    <dx-load-indicator
                      :visible="loading"
                      class="button-indicator"
                    />
                    <span class="dx-button-text">{{ $t('authentication.btnEnter') }}</span>
                  </div>
                </template>
              </dx-button>
            </div>
          </div>

          <reqRestorePasswordForm
            v-else
            @update:isAuthentication="isAuthentication = $event"
          />

          <div class="version">{{ appVersion }}</div>
        </div>
      </div>
      <div class="logo">
        <img src="../assets/Nicotech.svg" alt="Nicotech">
        <div class="logo-designation">Nicotech</div>
      </div>
    </div>

    <div class="background-info">
      <div v-if="isCloudMode">
        <div class="info">
          <div class="sub-info">{{ $t('authentication.info1') }}<br>{{ $t('authentication.info2') }}</div>
          <DxButton
            class="button-info"
            :text="$t('authentication.btnReq')"
            @click="togglePopup"
          />
        </div>

        <DxPopup
          :visible="isPopupVisible"
          :drag-enabled="false"
          :hide-on-outside-click="false"
          :show-close-button="false"
          :show-title="true"
          :width="520"
          :height="480"
          :title="$t('authentication.reqFormHeader')"
          @hiding="onHiding"
        >
          <DxToolbarItem
            widget="dxButton"
            toolbar="bottom"
            location="before"
            :options="editorsOptions.emailSend"
          />
          <DxToolbarItem
            widget="dxButton"
            toolbar="bottom"
            location="after"
            :options="editorsOptions.closeButton"
          />

          <template #btn-send>
            <div class="button-container">
              <dx-load-indicator
                :visible="isSend"
                class="button-indicator"
              />
              <span class="dx-button-text">{{ $t('general.send') }}</span>
            </div>
          </template>

          <dx-form
            :form-data="userRequest"
            :read-only="isSend"
            labelMode="floating"
            @initialized="saveFormReqInstance"
          >
            <dx-simple-item
              data-field="email"
              :editor-options="editorsOptions.email"
            >
              <dx-label :text="$t('authentication.email')"/>
              <dx-required-rule :message="$t('general.mandatory')"/>
              <dx-validation-rule
                type="pattern"
                :pattern="validationPatterns.email"
                :message="$t('general.badEmail')"
              />
            </dx-simple-item>

            <dx-simple-item
              data-field="fullName"
              :editor-options="editorsOptions.fullName"
            >
              <dx-label :text="$t('authentication.fullName')"/>
              <dx-required-rule :message="$t('general.mandatory')"/>
            </dx-simple-item>

            <dx-simple-item
              data-field="phone"
              :editor-options="editorsOptions.phone"
            >
              <dx-label :text="$t('authentication.phone')"/>
              <dx-required-rule :message="$t('general.mandatory')"/>
              <dx-validation-rule
                type="pattern"
                :pattern="validationPatterns.phone"
                :message="$t('general.badPhone')"
              />
            </dx-simple-item>

            <dx-simple-item
              data-field="company"
              :editor-options="editorsOptions.company"
            >
              <dx-label :text="$t('authentication.company')"/>
              <dx-required-rule :message="$t('general.mandatory')"/>
            </dx-simple-item>

            <dx-simple-item
              data-field="comment"
              editor-type="dxTextArea"
              :editor-options="editorsOptions.comment"
            >
              <dx-label :text="$t('authentication.comment')"/>
            </dx-simple-item>
          </dx-form>
        </DxPopup>
      </div>

    </div>
  </div>
</template>

<script>
import appInfo from "../app-info";

import DxForm, {
  DxSimpleItem,
  DxGroupItem,
  DxLabel,
  DxRequiredRule,
  DxValidationRule
} from 'devextreme-vue/form';
import { DxCheckBox, DxButton, DxLoadIndicator } from 'devextreme-vue';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';

import { notifier } from '@/shared/lib/notifier/index';

import { auth, getUserMe, getUserLicensesRelevant } from "@/services/api";
import axios from "axios";

import { validationPatterns } from "@/utils/validation-patterns";
import eyeOpen from "@/assets/eyeopen.svg";
import eyeClose from "@/assets/eyeclose.svg";
import reqRestorePasswordForm from './req-restore-password-form';

export default {
  components: {
    DxForm, DxSimpleItem, DxGroupItem, DxLabel, DxRequiredRule, DxValidationRule, DxCheckBox, DxButton, DxLoadIndicator, DxPopup, DxToolbarItem, reqRestorePasswordForm
  },
  data() {
    return {
      validationPatterns,
      formInstance: null,
      formReqInstance: null,
      isPopupVisible: false,
      isAuthentication: true,
      userRequest: {},
      userCredentials: {
        login: window.localStorage[`${document.location.pathname}-login`] || "",
        password: window.localStorage[`${document.location.pathname}-password`] || "",
        rememberUser: !!window.localStorage
                      && !!window.localStorage[`${document.location.pathname}-login`]
                      && !!window.localStorage[`${document.location.pathname}-password`]
                      || false,
      },
      loading: false,
      isSend: false,
      passwordMode: 'password',
      passwordIcon: eyeClose,
      version: this.$store.getters.version,
    };
  },
  computed: {
    isCloudMode() {
      return this.$store.getters.isCloudMode;
    },
    editorsOptions() {
      return {
        login: {
          inputAttr: { autocomplete:'username' }
        },
        password: {
          mode: this.passwordMode,
          inputAttr: { autocomplete:'new-password' },
          buttons : [{
            name: 'password',
            options:{
              icon: this.passwordIcon,
              stylingMode: 'text',
              type: 'default',
              onClick: () => {
                // eslint-disable-next-line
                this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
                // eslint-disable-next-line
                this.passwordIcon = this.passwordIcon === eyeClose ? eyeOpen : eyeClose;
              }
            }
          }]
        },
        emailSend: {
          icon: 'email',
          type: 'default',
          stylingMode: 'contained',
          disabled: this.isSend,
          template: 'btn-send',
          onClick: () => {
            if (!this.validateReqform()) {
              return;
            }
            // eslint-disable-next-line
            this.isSend = true;

            axios.post(`${this.$store.getters.apiPath}/instance/request-demo`, this.userRequest)
            .then(() => {
              notifier.showSuccess(this.$t('authentication.successRequest'));
              // eslint-disable-next-line
              this.isPopupVisible = false;
              // eslint-disable-next-line
              this.isSend = false;
              // eslint-disable-next-line
              this.userRequest = {};
            })
          },
        },
        closeButton: {
          text: this.$t('general.cancel'),
          stylingMode: 'outlined',
          type: 'normal',
          onClick: this.togglePopup
        },
        email: { inputAttr: { autocomplete:'off' }, maxLength: 128 },
        fullName: {maxLength: 128},
        phone: { inputAttr: { autocomplete:'off' }, maxLength: 128 },
        company: {maxLength: 128},
        comment: {
          maxLength: 256,
          height: 75
        }
      }
    },
    appVersion () {
      if (this.version)
        return `${this.version.appVersion}.${appInfo.feBuildNum}/${this.version.buildNumber} [db:${this.version.dbInfo.schema}.${this.version.dbInfo.fwInfo.schemaVersion}, vrs: ${this.version.modelsVersion ? this.version.modelsVersion.versionNumber : ''}]`;
      else
        return `v.${appInfo.feBuildNum}/XX [db:xxxx]`;
    }
  },
  methods: {
    saveFormInstance(e) {
      this.formInstance = e.component;
    },
    saveFormReqInstance(e) {
      this.formReqInstance = e.component;
    },
    onHiding() {
      this.isPopupVisible = false;
    },
    togglePopup() {
      this.isPopupVisible = this.isPopupVisible ? false : true;
    },
    forgotPassClick() {
      this.isAuthentication = false;
    },
    validate() {
      const result = this.formInstance.validate();
      return result.isValid;
    },
    validateReqform() {
      const result = this.formReqInstance.validate();
      return result.isValid;
    },
    async doLogin() {
      try {
        this.loading = true;
        const { login, password } = this.userCredentials;

        const user = await auth(login, password);

        const userWithLocations = await getUserMe();
        user.locations =  userWithLocations.locations;
        this.$store.commit("setUser", user);

        const license = await getUserLicensesRelevant();
        this.$store.commit("setUserLicensesRelevant", license);

        if (this.userCredentials.rememberUser && window.localStorage) {
          window.localStorage[`${document.location.pathname}-login`] = login;
          window.localStorage[`${document.location.pathname}-password`] = password;
        } else if (window.localStorage) {
          delete window.localStorage[`${document.location.pathname}-login`];
          delete window.localStorage[`${document.location.pathname}-password`];
        }

        if (this.$route.query.redirect) {
          this.$router.push(this.$route.query.redirect);
        } else {
          this.$router.push("home");
        }
      } catch (err) {
        notifier.showError(err.message);
      } finally {
        this.loading = false;
      }
    },
    onLoginClick() {
      if (!this.validate()) {
        return;
      }
      this.doLogin();
    },
    async loadVersion() {
      try {
        const promise = new Promise((resolve) => {
          setTimeout(() => {
            resolve(this.$store.getters.apiPath);

          }, 250);
        });

        promise.then(api => {
          axios.get(`${api}/options/build-version/`)
            .then((response) => {
            this.version = response.data;
            this.$store.commit("setVersion", response.data);
          });

          axios.get(`${api}/options/cloud-mode.enabled`)
          .then((response) => {
            this.$store.commit("setIsCloudMode", response.data);
          })
        })
      } catch (err) {
        // eslint-disable-next-line
        console.error("loadVersion", err);
        this.$store.commit("setVersion", `${appInfo.title}.${appInfo.feBuildNum}/XX [db:xxxx]`);
      }
    },
  },
  created() {
    this.loadVersion();
  }
};
</script>

<style lang="scss">
@import "../themes/generated/variables.base.scss";
@import url("../themes/fonts.css");

.login-form {
  display: flex;
  flex-direction: row;
  min-height: 100vh;
  color: #20304A;

  .background-form {
    display: flex;
    flex-direction: column;
    flex: 0 0 50%;
    align-items: center;
    justify-content: flex-end;
    background-color: #20304A;

    .form {
      align-content: center;
      margin: auto;
      padding: 48px 48px 50px 48px;
      border-radius: 20px;
      background-color: #fff;
      box-shadow: 0 5px 10px 0 rgba(0, 0, 0, .5);

      .container-form {
        display: flex;
        flex-direction: column;
        max-width: 300px;

        .login-header {
          text-align: center;
          margin-bottom: 8px;
          font-family: 'Golos Text', sans-serif;
          font-size: 20px;
          font-weight: 400;
          line-height: 24px;
        }

        .version {
          align-self: center;
          color: #999999;
          font-family: 'Golos Text', sans-serif;
          font-size: 12px;
          font-weight: 400;
          line-height: 20px;
          margin-top: 46px;
        }

        .dx-button-mode-contained.dx-button-default {
          height: 40px;
          border-radius: 10px;
          background-color: #20304A;

          &:hover {
            background-color: #0072A7;
          }
        }
      }
    }

    .logo {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-bottom: 60px;
      color: #fff;

      .logo-designation {
        color: rgb(255, 255, 255);
        font-family: 'Golos Text', sans-serif;
        font-size: 36px;
        font-weight: 700;
        line-height: 49px;
      }
    }
  }

  .background-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    background-image: url('../assets/background-log-form-ton.jpeg');
    background-position-x: center;
    background-repeat: no-repeat;
    background-size: cover;
    padding: 88px 48px 75px 55px;

    .info {
      display: flex;
      padding: 36px 24px;
      background-color: #fff;
      border-radius: 20px;
      flex-direction: column;
      align-items: center;
      gap: 8px;

      .sub-info {
        text-align: center;
        font-family: 'Golos Text', 'Arial', sans-serif;
        font-size: 20px;
        line-height: 24px;
        font-weight: 400;
      }
      .button-info {
        color: #00A4F9;
        font-family: 'Golos Text', sans-serif;
        font-size: 20px;
        font-weight: 500;
        line-height: 24px;
      }
    }
  }
}

#submit {
  font-family: 'Golos Text', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}
.button-indicator {
  height: 24px;
  width: 24px;
  display: inline-block;
  vertical-align: middle;
  margin-right: 5px;
  .dx-loadindicator-icon {
    .dx-loadindicator-segment {
      background: white;
    }
  }
}

.button-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

.validation-margin {
  margin-bottom: 2.5rem;
}

</style>
